import React, {Component} from 'react';
import {getCategoryPercentages, getCardSpending, getDailySpending} from '../util/APIUtils';
import {DatePicker, Select, Switch} from 'antd';
import {convertToLocalFormat} from "../util/utils";
import moment from "moment";
import 'moment-timezone';
import {dateFormat} from "../constants";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getUserTimezone } from '../util/timezone';

const {MonthPicker} = DatePicker;
const {Option} = Select;

class ReportOverview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            categoryPercentages: [],
            cardSpending: [],
            dailySpending: [],
            date: moment().format(dateFormat),
            chartType: 'bar',
            horizontal: false
        };
    }

    componentDidMount() {
        const date = moment().format(dateFormat);
        this.loadData(date);
    }

    loadData = (date) => {
        getCategoryPercentages(date).then(response => {
            this.setState({categoryPercentages: response});
        });
        getCardSpending(date).then(response => {
            this.setState({cardSpending: response});
        });
        getDailySpending(date).then(response => {
            this.setState({dailySpending: response});
        });
    }

    handleInputDateChange = (date) => {
        if (!date) return;
        const datef = moment(date).format(dateFormat);
        this.setState({date: datef}, () => {
            this.loadData(datef);
        });
    }

    getCategoryColor = (category) => {
        const colorMap = {
            'Food': '#ff7875',
            'Shopping': '#ffa940',
            'Housing': '#597ef7',
            'Transportation': '#73d13d',
            'Healthcare': '#ff85c0',
            'Education': '#9254de',
            'Entertainment': '#ffc53d',
            'Utilities': '#36cfc9',
            'Phone': '#5cdbd3',
        };
        return colorMap[category] || '#95de64';
    }

    render() {
        const {categoryPercentages, cardSpending, dailySpending, date, chartType, horizontal} = this.state;
        
        return (
            <div style={{ background: '#f5f5f5', minHeight: '100vh', paddingBottom: '80px' }}>
                {/* Header */}
                <div style={{ 
                    background: 'white',
                    padding: '20px 24px',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#262626', marginBottom: '4px' }}>
                                Reports
                            </div>
                            <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                {moment(date).format("MMMM YYYY")}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <MonthPicker 
                                onChange={this.handleInputDateChange} 
                                defaultValue={moment(this.state.date)}
                                picker="month"
                                size="default"
                                style={{ width: '140px' }}
                            />
                            <Switch 
                                checked={horizontal}
                                onChange={(checked) => this.setState({ horizontal: checked })}
                                checkedChildren="H"
                                unCheckedChildren="V"
                            />
                            <Select 
                                value={chartType} 
                                onChange={(value) => this.setState({ chartType: value })}
                                style={{ width: 90 }}
                            >
                                <Option value="bar">Bar</Option>
                                <Option value="pie">Pie</Option>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Charts Grid */}
                <div style={{ padding: '24px' }}>
                    <div style={{ display: 'grid', gap: '24px' }}>
                        {/* Category Chart */}
                        {categoryPercentages.length > 0 && (
                            <div style={{ 
                                background: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '20px' }}>
                                    By Category
                                </div>
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartType === 'bar' ? (
                                        <BarChart data={categoryPercentages.map(item => ({
                                            name: item.category,
                                            value: item.total
                                        }))} layout={horizontal ? 'vertical' : 'horizontal'}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            {horizontal ? (
                                                <>
                                                    <XAxis type="number" tick={{ fontSize: 12 }} />
                                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                                                </>
                                            ) : (
                                                <>
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                </>
                                            )}
                                            <Tooltip formatter={(value) => `$${convertToLocalFormat(value, 0)}`} />
                                            <Bar dataKey="value" name="Amount" radius={horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]}>
                                                {categoryPercentages.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={this.getCategoryColor(entry.category)} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    ) : (
                                        <PieChart>
                                            <Pie
                                                data={categoryPercentages.map(item => ({
                                                    name: item.category,
                                                    value: item.total
                                                }))}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={100}
                                                dataKey="value"
                                            >
                                                {categoryPercentages.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={this.getCategoryColor(entry.category)} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `$${convertToLocalFormat(value, 0)}`} />
                                        </PieChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Card Chart */}
                        {cardSpending.length > 0 && (
                            <div style={{ 
                                background: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '20px' }}>
                                    By Card
                                </div>
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartType === 'bar' ? (
                                        <BarChart data={cardSpending.map(item => ({
                                            name: item.cardName,
                                            value: item.total
                                        }))} layout={horizontal ? 'vertical' : 'horizontal'}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            {horizontal ? (
                                                <>
                                                    <XAxis type="number" tick={{ fontSize: 12 }} />
                                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                                                </>
                                            ) : (
                                                <>
                                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                </>
                                            )}
                                            <Tooltip formatter={(value) => `$${convertToLocalFormat(value, 0)}`} />
                                            <Bar dataKey="value" name="Amount" fill="#1890ff" radius={horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]} />
                                        </BarChart>
                                    ) : (
                                        <PieChart>
                                            <Pie
                                                data={cardSpending.map(item => ({
                                                    name: item.cardName,
                                                    value: item.total
                                                }))}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={100}
                                                dataKey="value"
                                                fill="#1890ff"
                                            />
                                            <Tooltip formatter={(value) => `$${convertToLocalFormat(value, 0)}`} />
                                        </PieChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        )}

                        {/* Daily Chart */}
                        {dailySpending.length > 0 && (
                            <div style={{ 
                                background: 'white',
                                borderRadius: '12px',
                                padding: '24px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '20px' }}>
                                    Daily Expenses
                                </div>
                                <ResponsiveContainer width="100%" height={300}>
                                    {chartType === 'bar' ? (
                                        <BarChart data={dailySpending.map(item => ({
                                            date: moment(item.date).tz(getUserTimezone()).format('D'),
                                            amount: item.total
                                        }))} layout={horizontal ? 'vertical' : 'horizontal'}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            {horizontal ? (
                                                <>
                                                    <XAxis type="number" tick={{ fontSize: 12 }} />
                                                    <YAxis type="category" dataKey="date" tick={{ fontSize: 11 }} width={40} />
                                                </>
                                            ) : (
                                                <>
                                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 12 }} />
                                                </>
                                            )}
                                            <Tooltip 
                                                formatter={(value) => [`$${convertToLocalFormat(value, 0)}`, 'Amount']}
                                            />
                                            <Bar dataKey="amount" fill="#1890ff" radius={horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]} />
                                        </BarChart>
                                    ) : (
                                        <PieChart>
                                            <Pie
                                                data={dailySpending.map(item => ({
                                                    name: moment(item.date).tz(getUserTimezone()).format('MMM D'),
                                                    value: item.total
                                                }))}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={100}
                                                dataKey="value"
                                                fill="#1890ff"
                                            />
                                            <Tooltip formatter={(value) => `$${convertToLocalFormat(value, 0)}`} />
                                        </PieChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default ReportOverview;
